import React from "react";
import { Link } from "react-router-dom";

/**
 * PrivacyExtension · iter 305 round 4
 *
 * Privacy policy SPECIFICA per l'estensione Chrome "Pronto Publisher".
 * Richiesta obbligatoria dal Chrome Web Store quando l'estensione chiede
 * permessi tipo storage/scripting/host access.
 *
 * URL pubblico: /privacy-extension (no auth, indicizzabile)
 * Linkato da:
 *   - Chrome Web Store listing dell'estensione
 *   - Page /dashboard/extension dentro Pronto
 *   - manifest.json dell'estensione (homepage_url)
 *
 * Tono: italiano professionale, leggibile da chiunque (non legalese).
 * Allineato al GDPR + Chrome Web Store Developer Program Policies.
 */
export default function PrivacyExtension() {
  const lastUpdated = "15 maggio 2026";

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="privacy-extension-page">
      {/* Header semplice — no AppShell perché è pagina pubblica */}
      <header className="border-b border-[#EAE7DF] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-editorial text-2xl tracking-tight text-[#1C1C1A]" data-testid="privacy-home-link">
            Pronto
          </Link>
          <Link
            to="/auth"
            className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
            data-testid="privacy-login-link"
          >
            Accedi →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-3">
          Estensione Chrome · Pronto Publisher
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl tracking-tight mb-3" data-testid="privacy-title">
          Informativa sulla privacy
        </h1>
        <p className="text-[13px] text-[#9A968B] italic mb-10">
          Ultimo aggiornamento: {lastUpdated}
        </p>

        <div className="prose-pronto space-y-8 text-[14.5px] leading-relaxed text-[#1C1C1A]">

          {/* Titolare */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Chi siamo</h2>
            <p>
              "Pronto Publisher" è un'estensione del browser sviluppata e distribuita
              da <strong>Pronto</strong> (Prontoimmo, P.IVA 03868770169, sede legale
              Via San Tomaso 41, 24121 Bergamo), piattaforma B2B per agenti
              immobiliari italiani.
              <br />
              Per qualsiasi richiesta — incluso il nome del legale rappresentante,
              esercizio dei diritti GDPR o segnalazioni — puoi scriverci a{" "}
              <a href="mailto:supporto@prontoimmo.it" className="text-[#7C8B6E] underline">
                supporto@prontoimmo.it
              </a>. Risponderemo entro 30 giorni.
            </p>
          </section>

          {/* Scopo */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">A cosa serve l'estensione</h2>
            <p>
              L'estensione aiuta gli agenti immobiliari a pubblicare i propri annunci
              sui portali italiani (Immobiliare.it e Idealista)
              compilando automaticamente i campi dei form di inserimento partendo dai
              dati già salvati nel loro account Pronto. <strong>Non leggiamo, non
              raccogliamo e non trasmettiamo a terzi le pagine web che visiti</strong> —
              l'estensione si attiva solo sulle pagine di inserimento annunci dei portali
              elencati nel <em>manifest</em> dell'estensione.
            </p>
          </section>

          {/* Dati raccolti */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Quali dati vengono trattati</h2>
            <p>L'estensione tratta esclusivamente questi dati:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Dati dell'annuncio</strong> che hai esplicitamente scelto di
                pubblicare (titolo, prezzo, mq, indirizzo, descrizione, classe energetica,
                foto): vengono letti dal tuo account Pronto e <strong>memorizzati
                localmente nel tuo browser</strong> tramite l'API
                <code className="px-1.5 py-0.5 mx-1 bg-[#F1F4ED] text-[12.5px] rounded font-mono">chrome.storage.local</code>
                per la durata massima di 3 ore (poi vengono cancellati automaticamente).
              </li>
              <li>
                <strong>Token di autenticazione Pronto</strong>: copiato temporaneamente
                in <code className="px-1.5 py-0.5 mx-1 bg-[#F1F4ED] text-[12.5px] rounded font-mono">chrome.storage.local</code>
                per consentire all'estensione di scaricare le foto dal tuo account.
                Anche questo viene cancellato dopo 3 ore di inattività.
              </li>
            </ul>
            <p className="mt-4">
              <strong>Quello che NON facciamo:</strong> non leggiamo né registriamo
              la cronologia di navigazione, non tracciamo siti che visiti diversi
              dai portali elencati, non sincronizziamo dati su nostri server,
              non condividiamo nulla con terze parti, non usiamo analitiche né
              cookie pubblicitari.
            </p>
          </section>

          {/* Permessi richiesti */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Permessi richiesti dall'estensione</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>storage</strong>: per salvare temporaneamente i dati
                dell'annuncio nel tuo browser (vedi sopra).
              </li>
              <li>
                <strong>scripting</strong>: per inserire il pannello di compilazione
                e riempire i campi del form sui soli portali elencati.
              </li>
              <li>
                <strong>host_permissions</strong> limitati a
                <code className="px-1.5 py-0.5 mx-1 bg-[#F1F4ED] text-[12.5px] rounded font-mono">immobiliare.it</code> e
                <code className="px-1.5 py-0.5 mx-1 bg-[#F1F4ED] text-[12.5px] rounded font-mono">idealista.it</code>:
                l'estensione si attiva esclusivamente su questi domini. Su qualsiasi
                altra pagina web l'estensione è completamente inattiva.
              </li>
            </ul>
          </section>

          {/* Basi giuridiche */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Basi giuridiche</h2>
            <p>
              Il trattamento si basa sul tuo consenso esplicito (installazione
              dell'estensione e click sui bottoni di compilazione) e sull'esecuzione
              del contratto in essere con Pronto (art. 6, lett. a) e b) del GDPR).
            </p>
          </section>

          {/* Conservazione */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Per quanto tempo conserviamo i dati</h2>
            <p>
              I dati nell'archivio locale del browser vengono cancellati automaticamente
              dopo <strong>3 ore di inattività</strong>. Puoi cancellarli manualmente
              in qualsiasi momento disinstallando l'estensione o cliccando su "Cancella
              dati estensione" nelle impostazioni di Chrome.
            </p>
          </section>

          {/* Diritti */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">I tuoi diritti</h2>
            <p>
              In qualità di interessato hai diritto in qualsiasi momento di:
              accesso, rettifica, cancellazione, limitazione del trattamento,
              portabilità, opposizione e revoca del consenso (art. 15-22 GDPR).
              Per esercitarli scrivici a{" "}
              <a href="mailto:supporto@prontoimmo.it" className="text-[#7C8B6E] underline">
                supporto@prontoimmo.it
              </a>{" "}
              — ti risponderemo entro 30 giorni.
              <br />
              Hai inoltre diritto di proporre reclamo al Garante per la protezione
              dei dati personali (
              <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-[#7C8B6E] underline">
                garanteprivacy.it
              </a>).
            </p>
          </section>

          {/* Sicurezza */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Sicurezza</h2>
            <p>
              I dati transitano sempre su connessione cifrata HTTPS. Lo storage locale
              del browser è isolato per origine e protetto dal sistema operativo;
              nessun altro sito o estensione può accedervi.
            </p>
          </section>

          {/* Modifiche */}
          <section>
            <h2 className="font-editorial text-2xl mt-0 mb-3">Modifiche all'informativa</h2>
            <p>
              Possiamo aggiornare questa informativa quando rilasciamo nuove versioni
              dell'estensione o quando cambiano i requisiti normativi. Trovi sempre la
              versione corrente a questo URL. La data di "Ultimo aggiornamento" in cima
              indica quando è cambiata l'ultima volta.
            </p>
          </section>
        </div>

        {/* Footer link */}
        <div className="mt-16 pt-8 border-t border-[#EAE7DF] flex items-center justify-between text-[12.5px] text-[#9A968B]">
          <Link to="/" className="hover:text-[#1C1C1A] transition-colors" data-testid="privacy-back-link">
            ← Torna alla home
          </Link>
          <a
            href="mailto:supporto@prontoimmo.it"
            className="hover:text-[#1C1C1A] transition-colors"
            data-testid="privacy-contact-link"
          >
            supporto@prontoimmo.it
          </a>
        </div>
      </main>
    </div>
  );
}
